import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getRandomKey } from "../hooks/useReload";
import {
    PlannedWorkShiftDto,
    PlannedWorkShiftQueryParameters,
} from "../models/plannedWorkShift/plannedWorkShift";
import { getPlannedWorkShifts } from "../services/plannedWorkShiftService";
import { DayBookingItemBeta } from "../models/dayBooking/dayBookingItemBeta";
import { DayBookingTypeItemBeta } from "../models/dayBookingType/dayBookingTypeItemBeta";
import { getDayBookingItems } from "../services/dayBookingService";
import { getDayBookingTypes } from "../services/dayBookingTypeService";

interface WorkShiftPlanningFilters {
    selectedEmployeeIds: string[];
    selectedEmployeeGroupId: string;
    selectedSalaryPeriodTypeId: string;
    selectedSalaryPeriodId: string;
    selectedVehicleGroupId: string;
    startDate: string;
    endDate: string;
    showUnemployable: boolean;
    showAllDayBookings: boolean;
}

interface WorkShiftPlanningSidebar {
    plannedShiftId: string | null;
}

interface WorkShiftPlanningState {
    reloadKey: number;
    routesReloadKey: number;
    filters: WorkShiftPlanningFilters;
    plannedWorkShifts: PlannedWorkShiftDto[];
    shiftsLoading: boolean;
    cardViewMode: "compact" | "expanded";
    rowExpanded: Record<string, boolean>;
    dayBookingsByEmployeeId: Record<string, DayBookingItemBeta[]>;
    dayBookingsLoading: boolean;
    dayBookingTypes: Record<string, DayBookingTypeItemBeta>;
    dayBookingTypesLoading: boolean;
    sidebar: WorkShiftPlanningSidebar;
}

export const initialState: WorkShiftPlanningState = {
    reloadKey: getRandomKey(),
    routesReloadKey: getRandomKey(),
    filters: {
        selectedEmployeeIds: [],
        selectedEmployeeGroupId: null,
        selectedSalaryPeriodTypeId: null,
        selectedSalaryPeriodId: null,
        selectedVehicleGroupId: null,
        startDate: null,
        endDate: null,
        showUnemployable: true,
        showAllDayBookings: false,
    },
    plannedWorkShifts: [],
    shiftsLoading: false,
    cardViewMode: "compact",
    rowExpanded: {},
    dayBookingsByEmployeeId: {},
    dayBookingsLoading: false,
    dayBookingTypes: {},
    dayBookingTypesLoading: false,
    sidebar: { plannedShiftId: null },
};

export const workShiftPlanningSlice = createSlice({
    name: "workShiftPlanning",
    initialState: initialState,
    reducers: {
        setPlannedWorkShifts: (
            state,
            action: PayloadAction<PlannedWorkShiftDto[]>
        ) => {
            state.plannedWorkShifts = action.payload;
        },
        setPlannedWorkShiftsLoading: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.shiftsLoading = action.payload;
        },
        reloadPlannedWorkShifts: (state) => {
            state.reloadKey = getRandomKey();
        },
        reloadRoutes: (state) => {
            state.routesReloadKey = getRandomKey();
        },
        updateWorkShiftPlanningFilters: (
            state,
            action: PayloadAction<Partial<WorkShiftPlanningFilters>>
        ) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        setCardViewMode: (state, action: PayloadAction<"compact" | "expanded">) => {
            state.cardViewMode = action.payload;
        },
        toggleRowExpanded: (state, action: PayloadAction<string>) => {
            if (!state.rowExpanded) state.rowExpanded = {};
            const id = action.payload ?? "null";
            state.rowExpanded[id] = !state.rowExpanded[id];
        },
        setAllRowsExpanded: (state, action: PayloadAction<boolean>) => {
            state.cardViewMode = action.payload ? "expanded" : "compact";
            state.rowExpanded = {};
        },
        clearDayBookings: (state) => {
            state.dayBookingsByEmployeeId = {};
            state.dayBookingsLoading = false;
        },
        setSidebarData: (state, action: PayloadAction<WorkShiftPlanningSidebar>) => {
            state.sidebar = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchDayBookingTypes.pending, (state) => {
            state.dayBookingTypesLoading = true;
        });
        builder.addCase(fetchDayBookingTypes.fulfilled, (state, action) => {
            state.dayBookingTypesLoading = false;
            state.dayBookingTypes = Object.fromEntries(
                action.payload.map((type) => [type.id, type])
            );
        });
        builder.addCase(fetchDayBookingTypes.rejected, (state) => {
            state.dayBookingTypesLoading = false;
        });

        builder.addCase(fetchDayBookings.pending, (state) => {
            state.dayBookingsLoading = true;
            state.dayBookingsByEmployeeId = {};
        });
        builder.addCase(fetchDayBookings.fulfilled, (state, action) => {
            state.dayBookingsLoading = false;
            const { employeeIds, bookings } = action.payload;
            const grouped: Record<string, DayBookingItemBeta[]> = {};

            (employeeIds ?? []).forEach((id) => {
                if (id) {
                    grouped[id] = [];
                }
            });

            bookings.forEach((booking) => {
                const employeeId = booking.employeeId;
                if (!employeeId) return;
                if (!grouped[employeeId]) {
                    grouped[employeeId] = [];
                }
                grouped[employeeId] = [...grouped[employeeId], booking];
            });

            Object.keys(grouped).forEach((key) => {
                grouped[key] = grouped[key].slice().sort((a, b) => {
                    const startA = a?.day ?? "";
                    const startB = b?.day ?? "";
                    if (startA !== startB) return startA?.localeCompare(startB);
                    const endA = a?.getEndDay?.() ?? a?.endDay ?? "";
                    const endB = b?.getEndDay?.() ?? b?.endDay ?? "";
                    if (endA !== endB) return endA?.localeCompare(endB);
                    return a?.id?.localeCompare(b?.id);
                });
            });

            state.dayBookingsByEmployeeId = grouped;
        });
        builder.addCase(fetchDayBookings.rejected, (state) => {
            state.dayBookingsLoading = false;
        });
    },
});

export const fetchPlannedWorkShifts = createAsyncThunk(
    "workShiftPlanning/list",
    async(params: PlannedWorkShiftQueryParameters, thunkApi) => {
        try {
            thunkApi.dispatch(setPlannedWorkShiftsLoading(true));
            const shifts = await getPlannedWorkShifts(params, thunkApi.signal);
            thunkApi.dispatch(setPlannedWorkShifts(shifts));
        } finally {
            thunkApi.dispatch(setPlannedWorkShiftsLoading(false));
        }
    }
);

export const fetchDayBookingTypes = createAsyncThunk(
    "workShiftPlanning/fetchDayBookingTypes",
    async() => {
        return await getDayBookingTypes();
    }
);

export const fetchDayBookings = createAsyncThunk(
    "workShiftPlanning/fetchDayBookings",
    async(
        params: { startDate: string; endDate: string; employeeIds: string[] },
        thunkApi
    ) => {
        const { startDate, endDate } = params;
        const employeeIds = (params.employeeIds ?? []).filter(
            (id) => !!id && id !== "null"
        );

        if (!startDate || !endDate || employeeIds.length === 0) {
            return { employeeIds, bookings: [] as DayBookingItemBeta[] };
        }

        const bookings = await getDayBookingItems(
            startDate,
            endDate,
            employeeIds,
            thunkApi.signal
        );

        return {
            employeeIds,
            bookings,
        };
    }
);

export const {
    setPlannedWorkShifts,
    setPlannedWorkShiftsLoading,
    reloadPlannedWorkShifts,
    reloadRoutes,
    updateWorkShiftPlanningFilters,
    setCardViewMode,
    toggleRowExpanded,
    setAllRowsExpanded,
    clearDayBookings,
    setSidebarData,
} = workShiftPlanningSlice.actions;

export const workShiftPlanningReducer = workShiftPlanningSlice.reducer;
